import { useEffect, useRef } from "react";
import { MatchPhone } from "refsix-js-models";

import { matchNeedsTimingsSetup } from "../../services/matchTimingsService";
import { submitMatchAndTagVenue } from "../../services/venueService";
import { canViewVenueRecords } from "../../pages/venue/venueHistory";

/**
 * Submit an untagged finished match the first time its result is opened (SCORZA-739 plan §7.1,
 * §7.3).
 *
 * The playing-periods screen is where a match normally gets submitted, because that is where its
 * stats become final. Two kinds of match never go through it:
 *
 *  - **Refereed live on the watch.** `matchNeedsTimingsSetup` is false once a match already has
 *    two or more half events, so the screen never opens. Those half events are real timing data,
 *    not a guess to be redone, which means the stats are already final and there is nothing to
 *    wait for.
 *  - **Historic matches.** The server backfill wrote their entries directly and never touched a
 *    match doc, so they have an entry on a board but no `venueRef` — and without the tag the
 *    result page can no longer find the board they are on. Viewing one heals it.
 *
 * Gated on `!venueRef` so this is one submission per match, not one per view, and the server's
 * `unchanged` path makes even a redundant one a single view read. Deliberately NOT gated on
 * `matchNeedsTimingsSetup`: a match that still wants its periods set is left alone, because
 * submitting it now would publish numbers that are about to change.
 */
/**
 * Training sessions whose withdrawal has been retried this app session (SCORZA-766). The edit
 * screen submits one once, on save; if that failed (offline, a stale read, a 5xx) the entry would
 * stay on its board, so viewing it retries — but only once per session, never on every visit.
 */
const trainingRetried = new Set<string>();

export function useFirstViewVenueSubmit(match: MatchPhone): void {
    // Per-mount, so a failed attempt retries on the next visit rather than in a render loop.
    const attempted = useRef(false);

    useEffect(() => {
        if (attempted.current) {
            return;
        }
        if (!canViewVenueRecords(match)) {
            return;
        }
        if (match.isTraining) {
            if (!match._id || trainingRetried.has(match._id)) {
                return;
            }
            trainingRetried.add(match._id);
            attempted.current = true;
            submitMatchAndTagVenue(match);
            return;
        }
        if (match.venueRef) {
            return;
        }
        if (matchNeedsTimingsSetup(match)) {
            return;
        }

        attempted.current = true;
        submitMatchAndTagVenue(match);
    }, [match]);
}
